import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconChevronLeft } from '../icons';
import { CodeBoxMark } from '../BrandLogo';

/**
 * DEFINE — the first screen of the flow, ported from the old Code Box `StageDefineIdeaV3`.
 *
 * One centered card, one big question, one forward action. The example chips matter more than they
 * look: a blank textarea is the single biggest drop-off point for a non-coder.
 *
 * Each suggestion shows a SHORT label and pastes a full, well-formed prompt. That is deliberate
 * teaching: the learner picks something readable, then sees in the textarea what a good brief
 * actually contains (who it is for + the core loop + the twist + the vibe) and edits from there.
 * Showing the full paragraph on the chip itself made the row unscannable and taught nothing.
 */

interface Suggestion {
  label: string;
  blurb: string;
  prompt: string;
}

const SUGGESTIONS: readonly Suggestion[] = [
  {
    label: 'Neon snake',
    blurb: 'Arcade game that speeds up',
    prompt:
      'A neon arcade snake game where every piece of food I eat makes the snake faster and leaves a glowing trail behind it. Rare golden food is worth five points but shrinks the play area for ten seconds. Dark synthwave colours, a chunky pixel score in the corner, and a "new best" celebration when I beat my high score.',
  },
  {
    label: 'Spanish flashcards',
    blurb: 'Study app with streaks',
    prompt:
      'A flashcard app that quizzes me on Spanish verbs before class. It shows one verb at a time, I try to recall the English, then tap to flip the card and mark whether I got it right. Cards I keep missing come back sooner. Show a daily streak and how many I have mastered, with calm colours and big readable type.',
  },
  {
    label: 'Lo-fi focus timer',
    blurb: 'Pomodoro with a streak tracker',
    prompt:
      'A pomodoro focus timer for studying: 25 minutes of work, then a 5 minute break, with a gentle chime at each switch. A big circular countdown fills as time passes, and I can name what I am working on. Track how many sessions I finish each day and show the last seven days as small bars. Warm, cosy, low-contrast look.',
  },
  {
    label: '90s trivia night',
    blurb: 'Quiz game for friends',
    prompt:
      'A trivia quiz about 1990s pop culture for game night with friends. Ten multiple-choice questions on music, TV and toys, one at a time with four options and a ten second timer. Right answers turn green and wrong ones show the correct one. Faster answers score more, and the end screen ranks everyone who played on this device.',
  },
  {
    label: 'Gem-collecting robot',
    blurb: 'Platformer with slimes to dodge',
    prompt:
      'A side-scrolling platformer where a small robot collects gems and dodges slimes. Arrow keys to move, space to jump, and a double jump once I find the upgrade. Three hearts, and touching a slime costs one. Each level ends at a door that only opens once every gem is collected. Bright cartoon look with bouncy movement.',
  },
  {
    label: 'Plant watering log',
    blurb: 'Tracker that reminds you',
    prompt:
      'An app to keep my houseplants alive. I add each plant with its name, a photo and how often it needs watering. The home screen shows which plants are thirsty today, and I tap one to mark it watered. Plants I have neglected drift to the top with a gentle nudge, not a scary alert. Soft greens and rounded cards.',
  },
  {
    label: 'Split the bill',
    blurb: 'Tool for group dinners',
    prompt:
      'A tool for splitting a restaurant bill with friends. I add each person, then add items and tap who shared each one. It works out what everybody owes including tax and tip, and I can round to whole amounts. Show a clear per-person summary I can read aloud at the table. Simple, uncluttered, and fast to use one-handed.',
  },
  {
    label: 'Endless sky runner',
    blurb: 'One-button dodging game',
    prompt:
      'A one-button endless runner where a paper plane glides through a canyon. Holding the mouse or space makes it rise and letting go makes it fall, so it is all about timing through gaps. The longer I survive the faster it gets, and passing close to a wall without hitting it scores bonus points. Soft pastel sky with parallax clouds.',
  },
];

export function StageDefine({
  name,
  busy,
  error,
  onStart,
}: {
  name?: string | null;
  busy: boolean;
  error?: string | null;
  onStart: (idea: string) => void;
}) {
  const [idea, setIdea] = useState('');
  const ready = idea.trim().length >= 4;

  return (
    <div className="grid min-h-full place-items-center bg-surface p-5 sm:p-8">
      <div className="anim-in w-full max-w-[720px]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CodeBoxMark className="h-6 w-auto" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted">Code Box</span>
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-muted transition hover:bg-page hover:text-ink"
          >
            <IconChevronLeft width={14} height={14} />
            Cancel
          </Link>
        </div>

        <h1 className="mt-3 text-[28px] font-extrabold leading-tight tracking-tight sm:text-[32px]">
          What do you want to build{name ? `, ${name}` : ''}?
        </h1>
        <p className="mt-2 text-sm text-muted">
          Tell me your app or game idea in plain English. I will ask a few quick questions, then build
          you a first version you can play with and share.
        </p>

        <textarea
          value={idea}
          onChange={e => setIdea(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && ready && !busy) onStart(idea.trim());
          }}
          rows={5}
          autoFocus
          placeholder="e.g. A retro snake game where the food grows tougher as you score higher"
          className="mt-5 w-full resize-y rounded-field bg-page/70 p-4 text-base outline-none ring-1 ring-transparent transition placeholder:text-muted focus:bg-surface focus:ring-accent/40 sm:text-sm"
        />

        {error && (
          <p className="mt-3 rounded-field border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted">
            Tip: say who it is for, what you do over and over, and the vibe.
          </p>
          <button
            type="button"
            onClick={() => ready && !busy && onStart(idea.trim())}
            disabled={!ready || busy}
            className="inline-flex h-11 w-full items-center justify-center rounded-field bg-sun px-6 text-sm font-extrabold text-brand transition hover:brightness-105 disabled:pointer-events-none disabled:opacity-50 sm:w-auto"
          >
            {busy ? 'Starting…' : 'Start →'}
          </button>
        </div>

        {idea.trim().length === 0 && (
          <div className="mt-8">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted">Try one of these</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {SUGGESTIONS.map(suggestion => (
                <button
                  key={suggestion.label}
                  type="button"
                  title="Use this idea — you can edit it before you start"
                  onClick={() => setIdea(suggestion.prompt)}
                  className="group rounded-field bg-page/70 px-3.5 py-3 text-left ring-1 ring-transparent transition hover:bg-sun/10 hover:ring-sun/50 active:scale-[0.99]"
                >
                  <span className="block text-[13px] font-bold text-ink">{suggestion.label}</span>
                  <span className="mt-0.5 block text-xs text-muted group-hover:text-accent-deep">
                    {suggestion.blurb}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
